import {
  Button,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Select,
  Space,
} from "antd";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import BDFlag from "../../../assets/icons/BDFlag";
import DateIcon from "../../../assets/icons/DateIcon";
import SelectDropdownLgIcon from "../../../assets/icons/SelectDropdownLgIcon";
import {
  dateFormat,
  dbDateFormat,
  genderList,
} from "../../../constants/constants";
import { useGetDepartmentsQuery } from "../../../features/department/departmentApi";
import { useGetDesignationsQuery } from "../../../features/designation/designationApi";
import {
  useActiveInvitedMutation,
  useGetEmployeeDetailsQuery,
} from "../../../features/employee/employeeApi";
import { useGetPayrollSettingQuery } from "../../../features/payrollSetting/payrollSettingApi";
import useError from "../../../hooks/useError";
import { ModalComponentProps } from "../../../types";
import {
  formatPhoneNumber,
  selectArrayOption,
  selectSearchOption,
  selectStringOption,
} from "../../../utils/utils";
import { CheckedData } from "../../common/sidebar/SidebarContent";
import CustomModal from "../../ui/CustomModal";
import Loader from "../../ui/Loader";

export default function InvitedEmployeeActive({
  handleClose,
  ...rest
}: ModalComponentProps<CheckedData>) {
  const [form] = Form.useForm();
  const { viewId } = useParams();
  const [departmentId, setDepartmentId] = useState<number | undefined>(
    undefined
  );
  const { data, isLoading: isDetailsLoading } = useGetEmployeeDetailsQuery(
    viewId || "",
    {
      skip: !viewId,
    }
  );
  const {
    data: payrollSettingData,
    isError: isPayrollSettingError,
    error: payrollSettingError,
  } = useGetPayrollSettingQuery();
  const {
    data: departmentData,
    isError: isDepartmentError,
    error: departmentError,
  } = useGetDepartmentsQuery({
    params: { limit: 0, skip: 0, is_published: 1 },
  });
  const {
    data: designationData,
    isError: isDesignationError,
    error: designationError,
  } = useGetDesignationsQuery(
    {
      params: {
        limit: 0,
        skip: 0,
        ...(departmentId
          ? { business_department_id: departmentId?.toString() || "" }
          : {}),
        is_published: 1,
      },
    },
    {
      skip: !departmentId,
    }
  );

  const [activeInvited, { isLoading, isError, isSuccess, error }] =
    useActiveInvitedMutation();

  // error toast message
  useError(isDepartmentError, departmentError, "Get department fail");
  useError(isDesignationError, designationError, "Get designation fail");
  useError(isError, error, "Profile updated fail");
  useError(
    isPayrollSettingError,
    payrollSettingError,
    "Gross salary configured data fail "
  );

  const detailsData = data?.data?.employee;

  const isShowModal =
    payrollSettingData?.data?.payroll_setting?.pay_components?.pay_schedule
      ?.is_enable;

  const onFinish = (values: any) => {
    const { join_date, department_id, mobile, ...rest } = values;
    const newData = {
      ...rest,
      mobile: formatPhoneNumber(mobile),
      department: departmentData?.data?.departments?.find(
        (department) => department?.id === department_id
      )?.name,
      department_id,
      join_date: join_date.format(dbDateFormat),
    };

    activeInvited({
      body: newData,
      id: Number(viewId),
    });
  };

  useEffect(() => {
    if (Object.keys(data || {})) {
      form.setFieldsValue({
        name: detailsData?.member?.profile?.name,
        mobile: detailsData?.mobile,
        gender: detailsData?.member?.profile?.gender,
        department_id: detailsData?.role?.department?.id,
        business_role_id: detailsData?.role?.id,
        join_date: detailsData?.join_date
          ? dayjs(detailsData?.join_date)
          : undefined,
      });

      setDepartmentId(detailsData?.role?.department?.id);
    }
  }, [
    data,
    detailsData?.join_date,
    detailsData?.member?.profile?.gender,
    detailsData?.member?.profile?.name,
    detailsData?.mobile,
    detailsData?.role?.department?.id,
    detailsData?.role?.id,
    form,
  ]);

  useEffect(() => {
    if (isSuccess) {
      handleClose();
    }
  }, [handleClose, isSuccess]);

  return (
    <CustomModal
      title="Fill Up the form to activate co-worker"
      {...rest}
      handleClose={handleClose}
      width={500}
      footer={null}
    >
      {isDetailsLoading ? (
        <Loader />
      ) : (
        <Form
          className="custom-form"
          layout="vertical"
          requiredMark="optional"
          size="large"
          onFinish={onFinish}
          form={form}
        >
          <Form.Item
            name="name"
            label="Name"
            className="mb-4"
            rules={[
              {
                required: true,
                message: "Please enter name",
              },
            ]}
          >
            <Input size="large" placeholder="Enter your name" />
          </Form.Item>
          <Form.Item
            name="mobile"
            label="Contact Number"
            className="mb-4"
            initialValue="+880"
            rules={[
              {
                validator: async (_: any, value: string) => {
                  if (!value) {
                    return Promise.reject(
                      new Error("Please enter phone number")
                    );
                  }
                  // if (!value) {
                  //   return Promise.resolve();
                  // }

                  const phoneRegex = /^(\+88)?(01[3-9])[0-9]{8}$/;
                  if (!phoneRegex.test(value)) {
                    return Promise.reject(
                      new Error("Please enter a valid Bangladeshi phone number")
                    );
                  }
                },
              },
            ]}
          >
            <Input prefix={<BDFlag />} size="large" placeholder="+8801" />
          </Form.Item>

          <Form.Item
            name="gender"
            label="Gender"
            className="mb-4"
            rules={[
              {
                required: true,
                message: "Please select a gender",
              },
            ]}
          >
            <Select
              placeholder="Select"
              size="large"
              allowClear
              showSearch
              filterOption={selectSearchOption}
              options={selectStringOption(genderList)}
              suffixIcon={<SelectDropdownLgIcon />}
            />
          </Form.Item>

          <Form.Item
            name="department_id"
            label="Department"
            className="mb-4"
            rules={[
              {
                required: true,
                message: "Please select department",
              },
            ]}
          >
            <Select
              placeholder="Select"
              size="large"
              allowClear
              showSearch
              filterOption={selectSearchOption}
              options={selectArrayOption(
                departmentData?.data?.departments || [],
                "name",
                "id"
              )}
              suffixIcon={<SelectDropdownLgIcon />}
              onChange={(val) => {
                setDepartmentId(val);
                form.setFieldsValue({ business_role_id: undefined });
              }}
            />
          </Form.Item>
          <Form.Item
            name="business_role_id"
            label="Designation"
            className="mb-4"
            rules={[
              {
                required: true,
                message: "Please select designation",
              },
            ]}
          >
            <Select
              placeholder="Select"
              size="large"
              allowClear
              showSearch
              filterOption={selectSearchOption}
              options={selectArrayOption(
                designationData?.data?.roles || [],
                "name",
                "id"
              )}
              suffixIcon={<SelectDropdownLgIcon />}
              disabled={!departmentId}
            />
          </Form.Item>
          {isShowModal ? (
            <Form.Item
              name="gross_salary"
              label="Gross Salary"
              className="mb-4"
              rules={[
                {
                  required: true,
                  message: "Please enter gross salary",
                },
                {
                  type: "number",
                  min: 0,
                  message: "Gross salary cannot negative",
                },
                {
                  type: "integer",
                  message: "Gross salary must be an integer",
                },
              ]}
            >
              <InputNumber
                style={{ width: "100%" }}
                placeholder="Enter gross salary"
              />
            </Form.Item>
          ) : null}
          <Form.Item
            name="join_date"
            label="Joining Date"
            className="mb-4"
            rules={[
              {
                required: true,
                message: "Please select joining date",
              },
            ]}
          >
            <DatePicker
              // defaultValue={dayjs(formatDate(), dateFormat)}
              format={dateFormat}
              suffixIcon={<DateIcon width={18} height={20} />}
              style={{ width: "100%" }}
              size="large"
            />
          </Form.Item>

          <Space className="w-full mt-2" size={12}>
            <Button
              className="normal-dark-button modal-button"
              onClick={handleClose}
              size="middle"
            >
              Cancel
            </Button>
            <Button
              className="modal-button"
              type="primary"
              htmlType="submit"
              size="middle"
              loading={isLoading}
            >
              Activate co-worker
            </Button>
          </Space>
        </Form>
      )}
    </CustomModal>
  );
}
